import { useContext, useMemo } from "react";

import { CommandStatus, CommandLang, convertCountryNameToCountryCode, CountryNames } from "@simplyk/common";
import { DateTime } from "luxon";
import { Control, useFormState, useWatch } from "react-hook-form";

import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";

import { useUpsertCommand } from "./useUpsertCommand";

import { DonationFormPaymentInput } from "@/common/types/donationForm";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getProductDonationRecurrenceFromStripe } from "@/components/PaymentProcessor/helper";

interface DonationFormUpdatedProps {
  control: Control<DonationFormPaymentInput>;
}

export const useDonationFormUpdated = ({ control }: DonationFormUpdatedProps) => {
  const { isDirty, isSubmitting, isSubmitSuccessful } = useFormState({ control });
  const { locale } = useLocaleContext();
  const {
    tip,
    displayedFormAmount,
    selectedPaymentMethod,
    stripeRecurrenceInterval,
    formData,
    formType,
    organization,
  } = useContext(FrontendFormContext);

  const email = useWatch({ control, name: "email" });
  const firstName = useWatch({ control, name: "firstName" });
  const lastName = useWatch({ control, name: "lastName" });
  const address = useWatch({ control, name: "address" });
  const city = useWatch({ control, name: "city" });
  const postalCode = useWatch({ control, name: "postalCode" });
  const region = useWatch({ control, name: "region" });
  const formCountry = useWatch({ control, name: "country" });
  const corporationName = useWatch({ control, name: "companyName" });
  const isCorporate = useWatch({ control, name: "giveAsOrganism" });
  const isPublicDonation = useWatch({ control, name: "isPublicDonation" }) ?? false;
  const tribute = useWatch({ control, name: "tribute" });
  const inHonourName = useWatch({ control, name: "inHonourName" });
  const notifySomeone = useWatch({ control, name: "notifySomeone" }) ?? false;
  const sendInHonourEmail = notifySomeone && Boolean(tribute);
  const inHonourEmailAddress = useWatch({ control, name: "inHonourNotifyEmail" });
  const inHonourEmailBody = useWatch({ control, name: "inHonourMessage" });

  const country = useMemo(
    () => (formCountry ? convertCountryNameToCountryCode(formCountry as CountryNames) : undefined),
    [formCountry]
  );

  useUpsertCommand(
    {
      email,
      firstName,
      lastName,
      address,
      city,
      postalCode,
      region,
      country,
      isCorporate,
      corporationName,
      productsAmount: displayedFormAmount ?? 0,
      totalAmount: (displayedFormAmount ?? 0) + (tip ?? 0),
      eligibleAmount: displayedFormAmount,
      tipAmount: tip,
      tipPercentage: 0,
      paymentMethod: selectedPaymentMethod,
      productDonation: {
        amount: displayedFormAmount,
        isAnonymous: !isPublicDonation,
        tribute,
        inHonourName,
        sendInHonourEmail,
        inHonourEmailAddress,
        inHonourEmailBody,
        recurrenceInterval: getProductDonationRecurrenceFromStripe(stripeRecurrenceInterval),
      },
      productTickets: null,
      productBids: null,
      formId: formData.id,
      formLang: CommandLang[locale],
      formType: formType || null,
      locale,
      organizationId: organization.id,
      status: CommandStatus.Open,
      userTimezone: DateTime.local().zoneName || "UTC",
    },
    isDirty,
    isSubmitting || isSubmitSuccessful
  );
};
